import React, { useEffect, useState } from 'react';
import { ShieldAlert, Dumbbell, Loader2, Target, Sparkles } from 'lucide-react';
import { Card, LabeledInput, LabeledSelect, Chip, Btn } from './shared';
import InjuryPicker from './InjuryPicker';
import { api } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';

const EQUIPMENT_OPTIONS = ['Bodyweight', 'Dumbbells', 'Resistance bands', 'Barbell', 'Kettlebell', 'Pull-up bar', 'Bench', 'Skipping rope', 'Yoga mat', 'Cardio machine'];
const PLAN_TYPES = [
  { id: 'cut', label: 'Cut', desc: 'Lose fat, keep strength', accent: 'from-[#ff7a1a] to-[#ff5e1a]' },
  { id: 'maintain', label: 'Maintain', desc: 'Hold and refine', accent: 'from-[#3a82f6] to-[#2563eb]' },
  { id: 'bulk', label: 'Bulk', desc: 'Add size and strength', accent: 'from-[#10b981] to-[#059669]' },
  { id: 'transform', label: 'Transform', desc: '5-stage long-haul journey', accent: 'from-[#a855f7] to-[#7c3aed]' },
];
const TRAINING_SPLITS = [
  { id: 'mixed',                label: 'Mixed',              desc: "AI picks freely — full-body, no fixed pattern." },
  { id: 'ppl_core',             label: 'PPL + Core',         desc: 'Push → Pull → Legs → Core. Classic 4-day rotation.' },
  { id: 'upper_lower',          label: 'Upper / Lower',      desc: '2-day rotation. Great for 4 sessions/week.' },
  { id: 'full_body',            label: 'Full Body',          desc: 'Every session hits everything. Best for 2-3 sessions/week.' },
  { id: 'calisthenics_mastery', label: 'Calisthenics',       desc: 'Bodyweight mastery: planche, front lever, pistol, handstand.' },
  { id: 'pilates_mastery',      label: 'Pilates (Mat)',      desc: 'Classical 34 — foundation → core → articulation → control.' },
  { id: 'reformer_mastery',     label: 'Reformer Pilates',   desc: 'Footwork → Hundred → Long/Short Box → Control Balance.' },
  { id: 'circuit_mastery',      label: 'Circuit Conditioning', desc: 'AMRAP / EMOM / Chipper benchmarks — engine + power.' },
  { id: 'running_mastery',      label: 'Running Fitness',    desc: 'Walk-jog → 5k → 10k → half → marathon → ultra prep.' },
];
const CALISTHENICS_LEVELS = [
  { id: 'beginner',     label: 'Beginner',     desc: 'Push-ups, assisted pull-ups, squats' },
  { id: 'intermediate', label: 'Intermediate', desc: 'Pull-ups, dips, pistols, tuck levers' },
  { id: 'advanced',     label: 'Advanced',     desc: 'One-arm work, full levers, planche' },
];

const DEFAULT_FORM = {
  plan_type: 'maintain', transform_stage: 0,
  training_split: 'ppl_core', calisthenics_level: 'intermediate',
  goal: 'general_health', sex: 'male', age: 30, height_cm: 178, weight_kg: 80,
  body_fat_pct: '', goal_body_fat_pct: '', weekly_rate_kg: 0.5,
  activity: 'moderate', target: '',
  injuries: [], injury_notes: '', equipment: ['Bodyweight', 'Dumbbells'],
};

const PlanTypeCard = ({ pt, active, onClick }) => (
  <button
    type="button" onClick={onClick}
    className={`text-left rounded-xl p-4 border transition-all ${
      active
        ? 'border-[#ff7a1a] bg-gradient-to-br from-[#1a0f06] to-[#0b0c10] shadow-lg shadow-[#ff7a1a]/10'
        : 'border-[#1f2937] bg-[#0b0c10] hover:border-[#ff7a1a]/60'
    }`}
  >
    <div className="flex items-center justify-between">
      <span className="text-white font-extrabold text-lg">{pt.label}</span>
      {active && <span className="text-[10px] uppercase font-bold text-[#ff7a1a]">Active</span>}
    </div>
    <p className="text-[#aab1bf] text-xs mt-1">{pt.desc}</p>
  </button>
);

const PlanForm = ({ initial, onSaved }) => {
  const { toast } = useToast();
  const [form, setForm] = useState(() => ({ ...DEFAULT_FORM, ...(initial || {}) }));
  const [saving, setSaving] = useState(false);
  const [stages, setStages] = useState([]);

  useEffect(() => {
    api.planTypes().then((d) => setStages(d?.transform_stages || [])).catch(() => {});
  }, []);

  useEffect(() => { if (initial) setForm((f) => ({ ...f, ...initial })); }, [initial]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setNum = (k) => (e) => setForm({ ...form, [k]: Number(e.target.value) });
  const toggleArr = (k, v) => {
    const arr = form[k] || [];
    setForm({ ...form, [k]: arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v] });
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        age: Number(form.age), height_cm: Number(form.height_cm), weight_kg: Number(form.weight_kg),
        weekly_rate_kg: Number(form.weekly_rate_kg) || 0.5,
        body_fat_pct: form.body_fat_pct === '' ? null : Number(form.body_fat_pct),
        goal_body_fat_pct: form.goal_body_fat_pct === '' ? null : Number(form.goal_body_fat_pct),
        transform_stage: Number(form.transform_stage) || 0,
        training_split: form.training_split || 'ppl_core',
        calisthenics_level: form.calisthenics_level || 'intermediate',
        injuries: form.injuries || [], equipment: form.equipment || [],
      };
      const saved = await api.upsertPlan(payload);
      toast({ title: 'Plan saved', description: 'Targets updated.' });
      onSaved(saved);
    } catch (err) {
      toast({ title: 'Could not save plan', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-6">
      <Card title="Choose your plan" icon={Target}>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {PLAN_TYPES.map((pt) => (
            <PlanTypeCard key={pt.id} pt={pt} active={form.plan_type === pt.id}
              onClick={() => setForm({ ...form, plan_type: pt.id })} />
          ))}
        </div>
        {form.plan_type === 'transform' && stages.length > 0 && (
          <div className="mt-5">
            <p className="text-[#cbd5e1] font-medium text-sm mb-2 flex items-center gap-1.5">
              <Sparkles className="w-4 h-4 text-[#ff7a1a]" /> Current Transform stage
            </p>
            <div className="flex flex-wrap gap-2">
              {stages.map((s, i) => (
                <Chip key={s.id} active={Number(form.transform_stage) === i}
                  onClick={() => setForm({ ...form, transform_stage: i })}>
                  {s.label} · {s.weeks}w
                </Chip>
              ))}
            </div>
          </div>
        )}

        <div className="mt-6">
          <p className="text-[#cbd5e1] font-medium text-sm mb-2">Training split</p>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-2">
            {TRAINING_SPLITS.map((s) => (
              <button
                type="button"
                key={s.id}
                onClick={() => setForm({ ...form, training_split: s.id })}
                data-testid={`split-${s.id}`}
                className={`text-left rounded-lg p-3 border transition-all ${
                  form.training_split === s.id
                    ? 'border-[#BEFF38] bg-[#BEFF38]/10'
                    : 'border-[#1f2937] bg-[#0b0c10] hover:border-[#BEFF38]/60'
                }`}
              >
                <p className={`font-extrabold text-sm ${form.training_split === s.id ? 'text-[#BEFF38]' : 'text-white'}`}>{s.label}</p>
                <p className="text-[#aab1bf] text-[11px] mt-0.5 leading-snug">{s.desc}</p>
              </button>
            ))}
          </div>
        </div>

        {form.training_split === 'calisthenics_mastery' && (
          <div className="mt-4">
            <p className="text-[#cbd5e1] font-medium text-sm mb-2">Your calisthenics level</p>
            <div className="grid grid-cols-3 gap-2">
              {CALISTHENICS_LEVELS.map((l) => (
                <button
                  type="button"
                  key={l.id}
                  onClick={() => setForm({ ...form, calisthenics_level: l.id })}
                  data-testid={`cali-${l.id}`}
                  className={`text-left rounded-lg p-2.5 border transition-all ${
                    form.calisthenics_level === l.id
                      ? 'border-[#BEFF38] bg-[#BEFF38]/10'
                      : 'border-[#1f2937] bg-[#0b0c10] hover:border-[#BEFF38]/60'
                  }`}
                >
                  <p className={`font-extrabold text-[11px] uppercase tracking-wider ${form.calisthenics_level === l.id ? 'text-[#BEFF38]' : 'text-white'}`}>{l.label}</p>
                  <p className="text-[#aab1bf] text-[10px] mt-0.5 leading-snug">{l.desc}</p>
                </button>
              ))}
            </div>
          </div>
        )}
      </Card>

      <Card title="Body & activity">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <LabeledSelect label="Goal" value={form.goal} onChange={set('goal')}>
            <option value="lose_fat">Lose fat</option>
            <option value="build_muscle">Build muscle</option>
            <option value="maintain">Maintain</option>
            <option value="improve_endurance">Improve endurance</option>
            <option value="general_health">General health</option>
          </LabeledSelect>
          <LabeledSelect label="Sex" value={form.sex} onChange={set('sex')}>
            <option value="male">Male</option>
            <option value="female">Female</option>
            <option value="other">Other</option>
          </LabeledSelect>
          <LabeledInput label="Age" type="number" min="12" max="100" value={form.age} onChange={setNum('age')} required />
          <LabeledInput label="Height (cm)" type="number" step="0.1" value={form.height_cm} onChange={setNum('height_cm')} required />
          <LabeledInput label="Weight (kg)" type="number" step="0.1" value={form.weight_kg} onChange={setNum('weight_kg')} required />
          <LabeledSelect label="Activity level" value={form.activity} onChange={set('activity')}>
            <option value="sedentary">Sedentary</option>
            <option value="light">Light</option>
            <option value="moderate">Moderate</option>
            <option value="active">Active</option>
            <option value="very_active">Very active</option>
          </LabeledSelect>
          <LabeledInput label="Body fat % (optional)" hint="Current estimate" type="number" step="0.1"
            value={form.body_fat_pct} onChange={set('body_fat_pct')} />
          <LabeledInput label="Goal body fat %" hint="Target estimate" type="number" step="0.1"
            value={form.goal_body_fat_pct} onChange={set('goal_body_fat_pct')} />
          <LabeledInput label="Weekly rate (kg)" hint="0.25 slow · 0.5 std · 1.0 fast" type="number" step="0.05"
            value={form.weekly_rate_kg} onChange={setNum('weekly_rate_kg')} />
        </div>
      </Card>

      <Card title="Injuries & restrictions" icon={ShieldAlert}>
        <p className="text-[#7c8597] text-xs -mt-2 mb-3">Tap any that apply. Your plan will avoid contraindicated movements. Reveal specific pathologies (e.g. rotator cuff, biceps tendinitis) for tighter filtering.</p>
        <InjuryPicker
          value={form.injuries || []}
          onChange={(next) => setForm({ ...form, injuries: next })}
        />
        <label className="flex flex-col gap-1.5 text-sm mt-4">
          <span className="text-[#cbd5e1] font-medium">Injury notes (optional)</span>
          <textarea
            rows={2} value={form.injury_notes || ''} onChange={(e) => setForm({ ...form, injury_notes: e.target.value })}
            placeholder="e.g. left knee meniscus, painful below 90° squat"
            className="bg-[#0b0c10] border border-[#2a2f3a] focus:border-[#ff7a1a] focus:ring-2 focus:ring-[#ff7a1a]/20 rounded-lg px-3 py-2.5 text-white outline-none"
          />
        </label>
      </Card>

      <Card title="Equipment available" icon={Dumbbell}>
        <p className="text-[#7c8597] text-xs -mt-2 mb-3">Only what you select will appear in your sessions.</p>
        <div className="flex flex-wrap gap-2">
          {EQUIPMENT_OPTIONS.map((i) => (
            <Chip key={i} active={(form.equipment || []).includes(i)} onClick={() => toggleArr('equipment', i)}>{i}</Chip>
          ))}
        </div>
        <label className="flex flex-col gap-1.5 text-sm mt-5">
          <span className="text-[#cbd5e1] font-medium">Target note (optional)</span>
          <textarea
            value={form.target} onChange={set('target')} rows={2}
            placeholder="e.g. drop 4kg by August, keep strength"
            className="bg-[#0b0c10] border border-[#2a2f3a] focus:border-[#ff7a1a] focus:ring-2 focus:ring-[#ff7a1a]/20 rounded-lg px-3 py-2.5 text-white outline-none"
          />
        </label>
      </Card>

      <div className="flex justify-end">
        <Btn type="submit" disabled={saving}>
          {saving && <Loader2 className="w-4 h-4 animate-spin" />} Save Plan
        </Btn>
      </div>
    </form>
  );
};

export default PlanForm;
